import { useQuery } from '@tanstack/react-query';
import { getCommunityStats } from '../api/community';
import { listFeedback } from '../api/feedback';
import { listBorrowings, listOverdueBorrowings, listRefunds, listReservations } from '../api/admin';

/**
 * 侧边栏菜单角标 + 概览待办共用的待处理计数 hooks。
 *
 * queryKey 与 DashboardPage / LibraryPendingCard 完全一致：
 * react-query 按 key 去重共享缓存，概览页和侧边栏同时挂载时不会重复请求。
 * 各 hook 的 enabled 由调用方按角色能力传入（与后端 @AdminRoles 白名单一致），
 * 无权限时不发请求、返回 undefined（菜单不显示角标）。
 */

export interface LibraryTodoCounts {
  requested: number;
  approved: number;
  returnRequested: number;
  overdue: number;
  borrowed: number;
  reservations: number;
}

/** 社区待办：待审帖/高风险/待审评论/待处理举报（/community/stats 单接口） */
export function useCommunityStats(enabled: boolean) {
  return useQuery({
    queryKey: ['community-stats'],
    queryFn: getCommunityStats,
    enabled,
    staleTime: 60_000,
  });
}

/** 待处理退款（REQUESTED）总数 */
export function useRefundPendingCount(enabled: boolean) {
  return useQuery({
    queryKey: ['dashboard-refunds-requested'],
    queryFn: () => listRefunds({ status: 'REQUESTED', page: 1, pageSize: 1 }),
    enabled,
    staleTime: 60_000,
  });
}

/** 新用户反馈（NEW）总数 */
export function useFeedbackNewCount(enabled: boolean) {
  return useQuery({
    queryKey: ['dashboard-feedback-new'],
    queryFn: () => listFeedback({ status: 'NEW', page: 1, pageSize: 1 }),
    enabled,
    staleTime: 60_000,
  });
}

/** 图书借阅六项计数（A-21 聚合端点上线前用列表 total 拼装） */
export function useLibraryTodoCounts(enabled: boolean) {
  return useQuery({
    queryKey: ['library-pending-counts'],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<LibraryTodoCounts> => {
      const [requested, approved, returnRequested, overdue, borrowed, reservations] =
        await Promise.all([
          listBorrowings({ status: 'REQUESTED', page: 1, pageSize: 1 }),
          listBorrowings({ status: 'APPROVED', page: 1, pageSize: 1 }),
          listBorrowings({ status: 'RETURN_REQUESTED', page: 1, pageSize: 1 }),
          listOverdueBorrowings({ page: 1, pageSize: 1 }),
          listBorrowings({ status: 'ACTIVE', page: 1, pageSize: 1 }),
          listReservations({ status: 'WAITING', page: 1, pageSize: 1 }),
        ]);
      return {
        requested: requested.total,
        approved: approved.total,
        returnRequested: returnRequested.total,
        overdue: overdue.total,
        borrowed: borrowed.total,
        reservations: reservations.total,
      };
    },
  });
}
